import { addHours, differenceInMinutes, isAfter, isBefore, startOfDay } from 'date-fns';
import { format } from 'date-fns-tz';
import { centralEuropeanNow, centralEuropeanTimezone, utcToCentralEuropean } from '../../../../play-core/utils/date-helper';
import { getBusinessUnit } from '../../../../play-core/utils/urn-helper';
import { usePath, useVideoConfig } from '../../config/VideoAppContext';
import { getTvGuideDefaultDate } from '../../pages/TvGuidePage/tv-guide-helpers';
import { isoDate, shortTimeFormat } from '../../utils/date-formatter';
// TODO move to tv-guide-helpers
export const defaultPixelsPerMinute = 7;
export const defaultTimelineIntervalInMin = 30;
export const defaultPlaceholderChannelCount = 3;
// extended for long-running show to have an extra marker at 6am
export const minutesPerDay = 25 * 60;
export const timelineRightSpace = 75;
export const primeTimeHours = 20;
export const primeTimeMinutes = 5;
export const startOfTvDayHour = 5;

// Grid View
export const gridGap = 6;
export const gridChannelWidth = 116;
export const gridChannelHeight = 106;
export const gridTimelineHeight = 42;
export const gridScrollbarHeight = 11;
export const minWidthForImage = 250;
export const hoverItemWidth = 350;
export const scrollFactor = 0.8; // value of 1.0 would scroll the entire TVP at once

// List View
export const listTimelineHeight = 64;
export const livePositionOfToday = (pixelsPerMinute, offset = gridChannelWidth) => {
  const livePosition = differenceInMinutes(centralEuropeanNow(), addHours(startOfDay(centralEuropeanNow()), startOfTvDayHour)) * pixelsPerMinute + offset;
  /**
   * Since a TV day does not necessarily start at 00:00, we need to check if the livePosition has a negative value.
   * If so, the live position is within the current TV day, but actually after 00:00 and we need to add 24 hours
   * to the current position. This is necessary to enable the live-indicator marker in the TV Guide to mark times
   * that are after 00:00.
   */
  return livePosition > 0 ? livePosition : livePosition + 24 * 60 * pixelsPerMinute;
};
export const getTimePosition = (time, pixelsPerMinute, offset = gridChannelWidth) => ((time.hours - startOfTvDayHour) * 60 + time.minutes) * pixelsPerMinute + offset;
export const getPrimeTimePosition = (pixelsPerMinute, offset = gridChannelWidth) => getTimePosition({
  hours: primeTimeHours,
  minutes: primeTimeMinutes
}, pixelsPerMinute, offset);
export const positionInTvDay = (timestamp, date, pixelsPerMinute, offset = gridChannelWidth) => Math.max(differenceInMinutes(utcToCentralEuropean(new Date(timestamp)), startOfTvDay(date)), 0) * pixelsPerMinute + offset;

/**
 * Example if device is in Greenwich Mean Time
 * date:    Fri Feb 24 2023 07:12:16 GMT+0000 (Greenwich Mean Time)
 * returns: Fri Feb 24 2023 05:00:00 GMT+0000 (Greenwich Mean Time)
 * --> CET: Fri Feb 24 2023 06:00:00 GMT+0100 (Mitteleuropäische Normalzeit)
 */
export const startOfTvDay = date => addHours(startOfDay(utcToCentralEuropean(new Date(date))), startOfTvDayHour);

/**
 * get tv start time regardless of the timezone of the device
 * Example if device is in Greenwich Mean Time
 * tvStartOfDayDate:  Fri Feb 24 2023 05:00:00 GMT+0000 (Greenwich Mean Time)
 * return:            Fri Feb 24 2023 04:00:00 GMT+0000 (Greenwich Mean Time)
 * --> CET:           Fri Feb 24 2023 05:00:00 GMT+0100 (Mitteleuropäische Normalzeit)
 */
export const startOfTvDayInCET = date => {
  const startOfDayDate = startOfDay(new Date(date));
  const tvStartOfDayDate = addHours(startOfDayDate, startOfTvDayHour);

  // change date to given timezone
  // replace for safari
  // valid:         2014-10-25T13:46:20+04:00
  // invalid date:  2014-10-25 13:46:20+04:00
  return new Date(format(tvStartOfDayDate, 'yyyy-MM-dd HH:mm:ssxxx', {
    timeZone: centralEuropeanTimezone
  }).replace(' ', 'T'));
};
export const isTodayCET = date => date === isoDate(centralEuropeanNow());
export const isTvDayTodayCET = date => date === isoDate(getTvGuideDefaultDate());
export const roundTo = (value, roundValue) => Math.ceil(value / roundValue) * roundValue;
export const scrollRightBy = (ref, amount) => {
  ref.current?.scrollBy({
    top: 0,
    left: amount,
    behavior: 'smooth'
  });
};
export const localTimeSpan = (from, to) => `${shortTimeFormat(utcToCentralEuropean(new Date(from)))} - ${shortTimeFormat(utcToCentralEuropean(new Date(to)))}`;
export const useLivestreamOrRedirectPath = (program, channel) => {
  const {
    baseUrl,
    businessUnit
  } = useVideoConfig();
  const isSameBusinessUnit = channel.businessUnit === businessUnit;
  const {
    livePath: livePathInternal,
    playLiveRedirectPath
  } = usePath();
  if (isLive(program) && channel.livestream) {
    return isSameBusinessUnit ? livePathInternal(channel.livestream) : playLiveRedirectPath(channel.businessUnit, baseUrl, channel.livestream);
  }
};
export const useDetailOrRedirectPath = (program, channel) => {
  const {
    baseUrl,
    businessUnit
  } = useVideoConfig();
  const {
    videoDetailPath: videoDetailInternal,
    playVideoDetailRedirectPath
  } = usePath();
  const mediaBusinessUnit = program.mediaUrn ? getBusinessUnit(program.mediaUrn) : channel.businessUnit;
  const isSameBusinessUnit = mediaBusinessUnit === businessUnit;
  const isLivestreamOfCurrentChannel = program.mediaUrn && program.mediaUrn === channel.livestream?.livestreamUrn;
  if (program.mediaUrn && !isLivestreamOfCurrentChannel) {
    return isSameBusinessUnit ? videoDetailInternal({
      urn: program.mediaUrn,
      title: (program.subtitle ?? program.lead)?.slice(0, 50),
      // best guess to set the title
      show: {
        title: program.title
      }
    }) : playVideoDetailRedirectPath(mediaBusinessUnit, baseUrl, {
      urn: program.mediaUrn,
      title: (program.subtitle ?? program.lead)?.slice(0, 50),
      // best guess to set the title
      showName: program.title
    });
  }
};
export const isLive = program => {
  const now = centralEuropeanNow();
  const start = utcToCentralEuropean(new Date(program.startTime));
  const end = utcToCentralEuropean(new Date(program.endTime));
  return isAfter(now, start) && isBefore(now, end);
};